import { FC } from "react";

import importSuccess from "../../../assets/fonts/importSuccess.svg";
import { Notification } from "../../../types/global/types";
import { useTranslation } from "../../../hooks/useTranslation";
import "./styles.scss";

type SkippedEntry = {
  group_name?: string;
  reason?: string;
};

type Props = {
  notification: Notification;
};

const humaniseReason = (reason: string, p: (k: string) => string): string => {
  switch (reason) {
    case "name_duplicate":
      return p("reason_name_duplicate");
    case "no_households":
      return p("reason_no_households");
    default:
      return reason;
  }
};

export const RoutesBatchCreatedNotification: FC<Props> = ({ notification }) => {
  const { p, t } = useTranslation("notifications");
  const data = notification.notes as any;

  // Support both shapes:
  // - notes returned by getBody() as an object with message / skipped_routes / counts
  // - legacy shape where notes is a plain string
  const message: string | null =
    typeof data === "object" && data !== null
      ? data.message ?? null
      : typeof data === "string"
        ? data
        : null;

  const skippedRoutes: SkippedEntry[] =
    typeof data === "object" && data !== null && Array.isArray(data.skipped_routes)
      ? data.skipped_routes
      : [];

  const createdCount: number =
    typeof data === "object" && data !== null
      ? data.created_count ?? 0
      : 0;

  const skippedCount: number =
    typeof data === "object" && data !== null
      ? data.skipped_count ?? 0
      : 0;

  const failedCount: number =
    typeof data === "object" && data !== null
      ? data.failed_count ?? 0
      : 0;

  const hasAnyActivity = createdCount > 0 || skippedCount > 0 || failedCount > 0;

  return (
    <div className="notification">
      {/* Header: icon + title ("Routes batch: X routes created, Y skipped") + timestamp */}
      <div className="notification__header">
        <div className="notification__reaction">
          <img src={importSuccess} alt="success" />
        </div>
        <div className="notification__title" style={{ width: "-webkit-fill-available" }}>
          <h3>
            {notification.title.startOfTitle}
            {notification.title.householdName}
            {notification.title.endOfTitle}
          </h3>
          <div className="notification__time">
            {notification.created_at_formatted}
          </div>
        </div>
      </div>

      {/* Stats row: Routes created: X  •  Skipped: Y  •  Failed: Z */}
      {hasAnyActivity && (
        <div className="notification__notes">
          <div className="notification__notes-header">
            {p("routes_created", { count: createdCount })}
            {skippedCount > 0 && (
              <span>
                {" "}&bull;{"  "}{p("skipped", { count: skippedCount })}
              </span>
            )}
            {failedCount > 0 && (
              <span>
                {" "}&bull;{"  "}{p("failed", { count: failedCount })}
              </span>
            )}
          </div>

          {/* Human-readable summary message */}
          {message && (
            <div className="notification__notes-body">{message}</div>
          )}
        </div>
      )}

      {/* Edge case: nothing at all */}
      {!hasAnyActivity && (
        <div className="notification__info">{p("no_routes_processed")}</div>
      )}

      {/* Skipped groups detail list — only shown when there are skipped entries */}
      {skippedRoutes.length > 0 && (
        <div className="notification__notes">
          <div className="notification__notes-header">{p("skipped_groups")}</div>
          <div className="notification__notes-body">
            {skippedRoutes.map((entry, idx) => (
              <div key={idx}>
                {entry.group_name ?? t("common.unknown")}
                {entry.reason ? ` \u2014 ${humaniseReason(entry.reason, p)}` : ""}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
